<template>
  <div class="switcher" :class="{ disabled }" @click="onSwitch">
    <span v-if="labelBeforeSwitcher" class="label-wrapper mr-6">
      <slot name="label">
        <ui-label
          v-if="label"
          :text="label"
          :text-bold="textBold"
          :help-tooltip="labelHelp"
        />
      </slot>
    </span>
    <span class="switcher-wrapper-with-planlimits">
      <span class="switcher-wrapper" :class="{ disabled }">
        <span class="switcher-background" :class="{ inactive: !isChecked }" />
        <span class="switcher-indicator" :class="{ inactive: !isChecked }" />
      </span>
      <ui-plan-limits
        v-if="showPlanLimitsIcon"
        class="plan-limits-icon"
      />
    </span>
    <span v-if="!labelBeforeSwitcher" class="label-wrapper ml-6">
      <slot name="label">
        <span class="label">{{ label }}</span>
      </slot>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePlanLimits, type PlanLimitsProp } from '@/composables';

const props = withDefaults(
  defineProps<{
    modelValue?: unknown;
    value?: unknown;
    labelHelp?: string;
    disabled?: boolean;
    textBold?: boolean;
    labelBeforeSwitcher?: boolean;
    label?: string;
    trueValue?: unknown;
    falseValue?: unknown;
    planLimits?: PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    labelHelp: '',
    disabled: false,
    textBold: false,
    labelBeforeSwitcher: true,
    label: '',
    trueValue: true,
    falseValue: false,
    planLimitsEditable: false,
  },
);

const emit = defineEmits<{
  change: [value: unknown];
  'update:modelValue': [value: unknown];
}>();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable,
);

const currentValue = computed(() =>
  props.modelValue !== undefined ? props.modelValue : props.value,
);

const isChecked = computed(() => {
  if (props.trueValue === currentValue.value) {
    return true;
  }
  if (props.falseValue === currentValue.value) {
    return false;
  }
  return undefined;
});

const switcherValue = computed(() => {
  if (currentValue.value === props.falseValue) {
    return props.trueValue;
  }
  if (currentValue.value === props.trueValue) {
    return props.falseValue;
  }
  return !currentValue.value;
});

const onSwitch = () => {
  if (showPlanLimitsIcon.value) {
    handleClickPlanLimits();
    return;
  }

  if (props.disabled) {
    return;
  }

  emit('change', switcherValue.value);
  emit('update:modelValue', switcherValue.value);
};
</script>

<style lang="scss" scoped>
.switcher {
  display: flex;
  cursor: pointer;
  align-items: center;
}

.switcher-wrapper {
  display: flex;
  position: relative;
  width: 32px;
  height: 16px;
  border-radius: 9999px;

  &.disabled {
    cursor: default;

    .switcher-background {
      background-color: var(--color-gray5);
    }

    .switcher-indicator {
      background-color: var(--color-gray20);
    }
  }

  &:focus {
    outline: 0;
  }
}

.switcher-background {
  display: inline-block;
  border-radius: 9999px;
  height: 100%;
  width: 100%;
  background-color: var(--color-green);

  &.inactive {
    background-color: var(--color-gray20);
  }
}

.switcher-indicator {
  position: absolute;
  height: 12px;
  width: 12px;
  right: 2px;
  bottom: 2px;
  background-color: var(--color-white);
  border-radius: 50%;

  &.inactive {
    left: 2px;
  }
}

.switcher-wrapper-with-planlimits {
  position: relative;
}

.plan-limits-icon {
  position: absolute;
  top: -4px;
  right: -4px;
}
</style>
